import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/EmptyState';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { toISODate, isScheduledOn, addDays } from '../lib/dates';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const isHabitDone = (habit, log) => {
  if (!log) return false;
  if (log.status === 'done') return habit.type === 'binary' ? true : Number(log.value || 0) >= Number(habit.target_value || 1);
  return habit.type !== 'binary' && Number(log.value || 0) >= Number(habit.target_value || 1);
};

export default function Calendar() {
  const { user } = useAuth();
  const [habits, setHabits] = useState([]);
  const [logsByDate, setLogsByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDate, setSelectedDate] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: h }, { data: l }] = await Promise.all([
      supabase.from('habits').select('*').is('archived_at', null),
      supabase.from('habit_logs').select('*'),
    ]);
    setHabits(h || []);
    const m = {};
    for (const log of l || []) { (m[log.log_date] ||= []).push(log); }
    setLogsByDate(m);
    setLoading(false);
  }, [user]);
  useEffect(() => { refresh(); }, [refresh]);

  const grid = useMemo(() => {
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstDay = new Date(year, month, 1);
    const start = new Date(firstDay);
    while (start.getDay() !== 0) start.setDate(start.getDate() - 1);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = addDays(start, i);
      const key = toISODate(d);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const inMonth = d.getMonth() === month;
      const inFuture = d > today;
      let scheduled = 0, done = 0;
      const colors = [];
      for (const h of habits) {
        if (isScheduledOn(h, d) && !inFuture) {
          scheduled++;
          const log = (logsByDate[key] || []).find((x) => x.habit_id === h.id);
          if (isHabitDone(h, log)) {
            done++;
            colors.push(h.color);
          }
        }
      }
      const ratio = scheduled === 0 ? 0 : done / scheduled;
      cells.push({ date: d, key, inMonth, inFuture, scheduled, done, ratio, colors: colors.slice(0, 4), isToday: key === toISODate(new Date()) });
    }
    return cells;
  }, [cursor, habits, logsByDate]);

  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  const prevMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
  const nextMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));

  const ringColor = (ratio) => {
    if (ratio === 0) return 'hsl(var(--border))';
    if (ratio < 0.34) return 'hsl(var(--heat-1))';
    if (ratio < 0.67) return 'hsl(var(--heat-2))';
    if (ratio < 1) return 'hsl(var(--heat-3))';
    return 'hsl(var(--heat-4))';
  };

  const detail = selectedDate ? (logsByDate[selectedDate] || []) : [];
  const selectedHabits = selectedDate ? habits.filter((h) => isScheduledOn(h, new Date(selectedDate))) : [];

  if (loading) return <Skeleton className="h-96 w-full rounded-2xl" data-testid="calendar-loading" />;
  if (habits.length === 0) return <EmptyState icon={CalendarDays} title="Nothing to calendar" description="Create a habit to see your daily history fill in." testid="calendar-empty" />;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Calendar</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Every day, at a glance</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} aria-label="Previous month" className="h-9 w-9 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="calendar-prev">
            <ChevronLeft className="h-5 w-5 mx-auto" />
          </button>
          <span className="font-heading font-medium w-44 text-center" data-testid="calendar-label">{monthLabel}</span>
          <button onClick={nextMonth} aria-label="Next month" className="h-9 w-9 rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="calendar-next">
            <ChevronRight className="h-5 w-5 mx-auto" />
          </button>
        </div>
      </div>

      {/* weekday header */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2 text-[11px] uppercase tracking-widest text-muted-foreground text-center">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
      </div>

      {/* grid */}
      <div className="grid grid-cols-7 gap-1 sm:gap-2" data-testid="calendar-grid">
        {grid.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setSelectedDate(c.key)}
            disabled={c.inFuture}
            className={`relative aspect-square rounded-xl border bg-card/30 p-1.5 text-left transition-all ${
              c.inMonth ? 'opacity-100' : 'opacity-35'
            } ${c.isToday ? 'border-primary' : 'border-border'} ${c.inFuture ? 'cursor-not-allowed' : 'hover:-translate-y-0.5'}`}
            style={{ borderTopWidth: '2px', borderTopColor: ringColor(c.ratio) }}
            data-testid={`calendar-day-${c.key}`}
          >
            <div className="flex justify-between items-start">
              <span className={`text-xs font-medium ${c.isToday ? 'text-primary' : ''}`}>{c.date.getDate()}</span>
              {c.scheduled > 0 && !c.inFuture && (
                <span className="text-[9px] font-mono text-muted-foreground">{c.done}/{c.scheduled}</span>
              )}
            </div>
            {c.colors.length > 0 && (
              <div className="absolute bottom-1.5 left-1.5 right-1.5 flex flex-wrap gap-0.5">
                {c.colors.map((col, k) => (
                  <span key={k} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: col }} />
                ))}
              </div>
            )}
          </button>
        ))}
      </div>

      {/* day detail */}
      {selectedDate && (
        <section className="rounded-2xl border border-border bg-card/30 p-5 sm:p-7" data-testid="calendar-detail">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-medium">{new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</h2>
            <button onClick={() => setSelectedDate(null)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="calendar-close-detail">Close</button>
          </div>
          {selectedHabits.length === 0 ? (
            <p className="text-sm text-muted-foreground">No habits were scheduled this day.</p>
          ) : (
            <div className="space-y-2">
              {selectedHabits.map((h) => {
                const log = detail.find((x) => x.habit_id === h.id);
                const done = isHabitDone(h, log);
                return (
                  <Link
                    key={h.id}
                    to={`/habits/${h.id}`}
                    className="flex items-center gap-3 py-2 hover:bg-muted/30 rounded-md px-2"
                    data-testid={`calendar-habit-${h.id}`}
                  >
                    <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: h.color }} />
                    <span className={`text-sm flex-1 ${done ? '' : 'text-muted-foreground'}`}>{h.name}</span>
                    <span className={`text-xs ${done ? 'text-primary' : log?.status === 'skipped' ? 'text-secondary' : 'text-muted-foreground'}`}>
                      {done ? '✓ Done' : log?.status === 'skipped' ? 'Skipped' : log ? `${log.value || 0}${h.unit ? ' ' + h.unit : ''}` : '—'}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
